// Shop-floor app for mechanics: phone-first, big buttons, no money anywhere.
import { WoPhotos, AddPhotos } from '../pages/WoPhotos';
import { ScanCount } from '../pages/ScanCount';
import { BaySelect } from '../pages/Bays';
import { useEffect, useRef, useState } from 'react';
import { floorOk, scanReceipt, api, useApi, useLocation, match, navigate, asset, uploadPhoto, hours, fmtClock, fmtTime, fmtDate, ago, toHundredths, toCents, dollars, WO_STATUSES, type Me, type Row, disposalNote, linkedNote } from '../lib';
import { Badge, Button, Icon, Loading, ErrorBox, Empty, Modal, Form, Field, MoneyField, Select, TextArea, PhotoGrid, CameraButton, SearchBox, useDebounced, useToast, Link, confirmAction, ReceiptPicker, ReceiptThumb, StoreField, useAutosave, SaveNote, PhotoThumbs, TechChip, ScanButton } from '../ui';
import { BodyPicker } from '../pages/Body';
import { MyTimeOff } from '../pages/StaffFile';
import { ChecksList, CheckBadge, CheckButtons, canCheck } from '../pages/Checks';
import { ChecklistButton } from '../pages/Pm';
import { PURCHASE_CATEGORIES, laborQty } from '../../shared/calc';
import { RecommendationList, EarlierRecommendations, AiWordingButton, AiLineButton, autoFix } from '../pages/Recommend';
import { NewWorkOrder, PartPickerModal, MileageEdit, RoadClock } from '../pages/WorkOrders';
import { PartsRuns } from '../pages/Runs';
import { partPhotos } from '../pages/Inventory';
import { qtyUom } from '../../shared/units';
import { logoUrl } from '../invoice-sheet';

export function MechanicApp({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const { path } = useLocation();
  const clock = useApi<Row>('/api/clock');
  const inbox = useApi<Row>('/api/notifications');
  useEffect(() => { const t = setInterval(() => { if (document.visibilityState === 'visible') inbox.reload(); }, 120000); return () => clearInterval(t); }, [inbox.reload]);
  let m;
  let page;
  if ((m = match('/floor/jobs/:id', path))) page = <Job id={m.id} me={me} clock={clock.data} reloadClock={clock.reload} />;
  else if (path === '/floor/parts') page = <PartsLookup />;
  else if (path === '/floor/shop') page = <ShopBoard me={me} />;
  else if (path === '/floor/count' && me.user.counter) page = <CountShelf />;
  else if (path === '/floor/receipts' && floorOk('receipts')) page = <Receipts />;
  else if (path === '/floor/clock') page = <Clock data={clock.data} reload={clock.reload} />;
  else if (path === '/floor/me') page = <MePage me={me} onSignOut={onSignOut} />;
  else if (path === '/floor/inbox') page = <Inbox onRead={() => inbox.reload()} />;
  else page = <Jobs me={me} />;
  const on = clock.data?.open;
  const tab = (to: string, icon: string, label: string) => {
    const active = to === '/floor' ? path === '/floor' || path === '/' || path.startsWith('/floor/jobs') : path.startsWith(to);
    return <Link to={to} className={active ? 'on' : ''}><Icon name={icon} size={22} /><span>{label}</span></Link>;
  };
  return (
    <div className="floor">
      <header className="floor-top">
        <div className="floor-brand"><img src={logoUrl(me.shop.logo_version)} alt="" /><div><strong>{String(me.shop.shop_name || 'Shop').split(/\s+/)[0].toUpperCase()}</strong><small>{me.user.name}</small></div></div>
        <button className="bell" aria-label={`Notifications${inbox.data?.unread ? ` (${inbox.data.unread} new)` : ''}`} onClick={() => navigate('/floor/inbox')}>
          <Icon name="bell" size={20} />{inbox.data?.unread > 0 && <b>{inbox.data.unread}</b>}</button>
        <button className={'clock-chip ' + (on ? 'on' : '')} onClick={() => navigate('/floor/clock')}>
          <span className="dot" />{on ? `On clock · ${fmtClock(on.clock_in)}` : 'Clocked out'}
        </button>
      </header>
      <main className="floor-main">{page}</main>
      <nav className="floor-nav">
        {tab('/floor', floorOk('check_in') ? 'truck' : 'clipboard', floorOk('check_in') ? 'Trucks' : 'Jobs')}
        {(floorOk('view_all') || floorOk('see_all')) && tab('/floor/shop', 'grid', 'Shop')}
        {floorOk('parts_search') && tab('/floor/parts', 'box', 'Parts')}
        {me.user.counter ? tab('/floor/count', 'clipboard', 'Count') : null}
        {tab('/floor/clock', 'clock', 'Clock')}
        {tab('/floor/me', 'users', 'Me')}
      </nav>
    </div>
  );
}

function Jobs({ me }: { me: Me }) {
  const seeAll = floorOk('see_all'), checkIn = floorOk('check_in'), boss = checkIn || floorOk('assign');
  const [view, setView] = useState<'mine' | 'open' | 'check'>(() => (localStorage.getItem('floor-view') as 'mine' | 'open') || (boss ? 'open' : 'mine'));
  const sup = me.shop.supervisor_on === '1' && canCheck();
  const checks = useApi<Row[]>(sup ? '/api/checks' : null, [view]);
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const shown = seeAll ? view : 'mine';
  const { data, error, reload } = useApi<Row[]>(`/api/work-orders?view=${shown === 'check' ? 'open' : shown}&q=${encodeURIComponent(dq)}`, [shown, dq]);
  const [creating, setCreating] = useState(false);
  useEffect(() => { try { localStorage.setItem('floor-view', view); } catch { /* private mode */ } }, [view]);
  useEffect(() => { const t = setInterval(() => { if (document.visibilityState === 'visible') reload(); }, 120000); return () => clearInterval(t); }, [reload]);
  return (
    <>
      <div className="floor-home-head">
        <h1 className="floor-h1">{shown === 'mine' ? 'My jobs' : shown === 'check' ? 'To check' : 'Trucks in the shop'}{data && shown !== 'check' ? <small> · {data.length}</small> : null}</h1>
        {checkIn && <button className="btn primary checkin-btn" onClick={() => setCreating(true)}><Icon name="plus" size={20} />Check in a truck</button>}
      </div>
      {(seeAll || sup) && <div className="seg">
        <button className={shown === 'mine' ? 'on' : ''} onClick={() => setView('mine')}>My jobs</button>
        {seeAll && <button className={shown === 'open' ? 'on' : ''} onClick={() => setView('open')}>All trucks</button>}
        {sup && <button className={shown === 'check' ? 'on' : ''} onClick={() => setView('check')}>To check{checks.data?.length ? ` (${checks.data.length})` : ''}</button>}
      </div>}
      {shown === 'check' && sup ? <ChecksList phone /> : <>
      <PushBanner />
      {(seeAll && shown !== 'mine') || (data?.length || 0) > 5 ? <SearchBox value={q} onChange={setQ} placeholder="Search unit #, WO #, customer…" /> : null}
      <ErrorBox error={error} onRetry={reload} />
      {!data ? <Loading /> : data.length ? (
        <div className={'cards' + (boss ? ' board' : '')}>
          {data.map(w => {
            const jobs = (w.jobs as Row[] | undefined) || [];
            const mine = jobs.filter(j => j.user_id === me.user.id && j.status !== 'done');
            const free = jobs.filter(j => !j.user_id).length;
            return (
            <button key={w.id} className={'job-card' + (mine.some(j => j.running) ? ' running' : '')} onClick={() => navigate('/floor/jobs/' + w.id)}>
              <div className="job-row"><b className="unit">#{w.unit || '—'}{w.trailer_unit && <small> + trailer #{w.trailer_unit}</small>}</b>{w.priority === 'Urgent' ? <Badge tone="red">URGENT</Badge> : <Badge>{w.status}</Badge>}</div>
              <div className="job-cust">{w.customer_name || 'Customer not set'}</div>
              {shown === 'mine' && mine.length > 0 ? <div className="my-job-list">{mine.map((j, i) => <span key={i} className={'mjl ' + (j.running ? 'run' : j.status)}>{j.running ? '● ' : ''}{j.title}</span>)}</div>
                : jobs.length ? <div className="truck-chips">{jobs.slice(0, 6).map((j, i) => (
                  <span key={i}><span className="jt">{j.title}</span><TechChip userId={j.user_id} name={j.name} meId={me.user.id} status={j.status} running={j.running} /></span>))}
                  {jobs.length > 6 && <small className="muted">+{jobs.length - 6} more</small>}</div>
                : w.my_jobs ? <div className="my-jobs"><Icon name="wrench" size={14} /> Your job: <b>{w.my_jobs}</b></div> : <p>{w.complaint}</p>}
              {boss && free > 0 && <div className="needs-assign">{free} job{free > 1 ? 's' : ''} not given to anyone</div>}
              <div className="job-meta">{w.photo_count > 0 && <span><Icon name="camera" size={14} /> {w.photo_count}</span>}{w.part_lines > 0 && <span><Icon name="box" size={14} /> {w.part_lines}</span>}<span>{ago(w.updated_at)}</span></div>
            </button>);
          })}
        </div>
      ) : <Empty>{shown === 'mine' ? (seeAll ? 'No jobs given to you. Look at “All trucks”.' : 'No jobs given to you right now. The supervisor gives out the jobs.') : 'No trucks in the shop.'}</Empty>}
      </>}
      {creating && <NewWorkOrder me={me} onClose={() => setCreating(false)} onCreated={id => navigate('/floor/jobs/' + id)} />}
    </>
  );
}

/**
 * One truck on the phone. Mechanics see only what they need: the jobs (Start / End records the time),
 * the parts they take for each job, photos, mileage. No prices, no hours to type, no 3C — the office does that when invoicing.
 * Mechanics can't remove lines; a mistake goes to the office.
 */
function Job({ id, me }: { id: string; me: Me; clock?: Row | null; reloadClock?: () => void }) {
  const { data, error, reload } = useApi<Row>(`/api/work-orders/${id}`);
  const pmChecks = useApi<Row[]>(`/api/work-orders/${id}/pm-checks`, [data]);
  const boss = floorOk('assign');
  const team = useApi<Row[]>(boss ? '/api/users' : null);
  const toast = useToast();
  const [modal, setModal] = useState<null | { part: string | null; photos?: boolean } | 'report' | 'job' | 'body'>(null);
  const [photoFor, setPhotoFor] = useState<string | null>(null);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const w = data.workOrder as Row;
  const locked = !!w.invoice_id;
  const jobs = data.jobs as Row[], parts = data.parts as Row[];
  const checks = (pmChecks.data || []) as Row[];
  const loose = parts.filter(p => !p.job_id || !jobs.some(j => j.id === p.job_id));
  // Work the office typed straight on the truck (not in a job) — shown so the mechanic knows what to do.
  const officeWork = (data.labor as Row[]).filter(l => !l.misc && (!l.job_id || !jobs.some(j => j.id === l.job_id)));
  // Mine (and jobs nobody has yet) first and open; other people's jobs folded away — the supervisor sees them all.
  const first = boss ? jobs : jobs.filter(j => j.assigned_to === me.user.id || !j.assigned_to);
  const others = boss ? [] : jobs.filter(j => j.assigned_to && j.assigned_to !== me.user.id);
  const mechs = ((team.data || []) as Row[]).filter(u => u.active !== 0 && u.role !== 'owner');
  const card = (j: Row) => <JobCard key={j.id} j={j} me={me} locked={locked} trailer={!!w.trailer_id} parts={parts.filter(p => p.job_id === j.id)} reload={reload}
    onPart={() => setModal({ part: j.id })} onFittings={() => setModal({ part: j.id, photos: true })} onBody={me.shop.body_on === '1' ? () => setModal('body') : undefined}
    onPhoto={() => setPhotoFor(j.id)} mechs={boss ? mechs : undefined} woId={id}
    check={checks.find(k => k.job_id === j.id)} onCheck={() => { pmChecks.reload(); reload(); }} />;
  const photos = (data.photos as Row[]).filter(p => p.kind === 'job');

  return (
    <div className="job">
      <button className="back-btn" onClick={() => navigate('/floor')}><Icon name="back" size={18} />Back</button>
      <div className="job-head">
        <div><h1>#{w.unit || w.unit_text || '—'}</h1><p>{w.customer_name || 'Customer not set'} · {w.number}</p>
          <p className="muted">{[w.unit_type, w.year, w.make, w.model].filter(Boolean).join(' ')}{w.vin ? ` · VIN …${String(w.vin).slice(-6)}` : ''}</p>
          {w.trailer_id && <p className="trailer-line">+ {w.trailer_type || 'Trailer'} <b>#{w.trailer_unit}</b>{w.trailer_vin ? ` · VIN …${String(w.trailer_vin).slice(-6)}` : ''}</p>}</div>
        {w.priority === 'Urgent' && <Badge>Urgent</Badge>}
      </div>
      {w.notes && <div className="complaint office"><small>Office notes</small>{w.notes}</div>}
      {locked && <div className="note-bar"><Icon name="check" size={16} />Invoiced — this truck is closed.</div>}
      {w.service === 'road' && <><h2 className="sec">Road service</h2><RoadClock w={w} locked={locked} onChanged={reload} /></>}

      <h2 className="sec">{boss ? 'Jobs on this truck' : 'My jobs on this truck'}</h2>
      {first.length ? <ul className="truck-jobs">{first.map(card)}</ul>
        : !officeWork.length && <p className="muted">{jobs.length ? 'No job of yours on this truck.' : floorOk('add_jobs') ? 'No jobs yet. Tap “Add job” — e.g. Tire change, Oil change — then Start.' : 'No jobs yet — the supervisor adds them.'}</p>}
      {officeWork.length > 0 && <div className="office-work"><small>Work written by the office</small>
        <ul>{officeWork.map(l => <li key={l.id}>{l.description}{l.mechanic_name ? <small className="muted"> · {l.mechanic_name}</small> : null}</li>)}</ul>
        {floorOk('add_jobs') && <p className="muted small">To track your time on it, tap “Add job”, give it the same name and press Start.</p>}</div>}
      {!locked && floorOk('add_jobs') && <Button kind="primary" icon="plus" className="full big-add" onClick={() => setModal('job')}>Add job</Button>}
      {others.length > 0 && <details className="fold" open={!first.length}><summary>Other jobs on this truck ({others.length})</summary><ul className="truck-jobs">{others.map(card)}</ul></details>}

      {loose.length > 0 && <details className="fold" open><summary>Parts not in a job ({loose.length})</summary>
        <PartLines parts={loose} me={me} woId={id} locked={locked} reload={reload} />
        <p className="muted small">Recorded on the truck without a job. The office can move them into the right job.</p></details>}

      <details className="fold"><summary>🔍 Found a problem? Tell the office{(data.recommendations as Row[]).length ? ` (${(data.recommendations as Row[]).length})` : ''}</summary>
        <EarlierRecommendations list={data.earlierRecommendations} office={false} />
        <RecommendationList woId={id} list={data.recommendations} office={false} locked={locked} myId={me.user.id} reload={reload} /></details>

      <details className="fold" open={photos.length === 0 && w.status === 'New'}><summary>📷 Photos ({photos.length})</summary>
        <WoPhotos w={w} photos={photos} jobs={jobs} office={false} myId={me.user.id} shotsRaw={me.shop.arrival_shots} reload={reload} locked={locked} />
        {(data.photos as Row[]).some(p => p.kind !== 'job') && <PhotoGrid photos={(data.photos as Row[]).filter(p => p.kind !== 'job')} />}</details>

      <details className="fold"><summary>Truck details — mileage, bay{floorOk('runs') ? ', parts run' : ''}</summary>
        {!locked && <label className="mech-mileage"><span>Mileage (from the dash)</span><MileageEdit key={w.id + (w.mileage || 0)} woId={id} value={w.mileage} prev={data.prevMileage} onSaved={reload} /></label>}
        {!locked && <div className="mech-bay"><BaySelect label="Where is the truck?" value={w.bay_id} onChange={async bay => { try { await api(`/api/work-orders/${id}`, { method: 'PATCH', body: { bay_id: bay || null } }); toast('Bay updated'); reload(); } catch (e) { toast((e as Error).message, 'err'); } }} /></div>}
        {!locked && floorOk('runs') && <PartsRuns woId={id} jobs={jobs} me={me} locked={locked} onChanged={reload} office={false} />}</details>

      {photoFor && <AddPhotos w={w} jobs={jobs} job={photoFor} start="diag" onClose={() => setPhotoFor(null)} onDone={() => { setPhotoFor(null); reload(); }} />}
      {modal && typeof modal === 'object' && <PartPickerModal title={`Part for: ${jobs.find(j => j.id === modal.part)?.title || 'this truck'}`} photos={modal.photos} startCategory={modal.photos ? 'fitting' : undefined} onClose={() => setModal(null)} onPick={async (part_id, qty) => {
        const r = await api(`/api/work-orders/${id}/parts`, { body: { part_id, qty, job_id: modal.part } }); setModal(null); toast(linkedNote(r) || disposalNote(r, false) || 'Part recorded on this job'); reload();
      }} onAnyway={floorOk('parts_new') ? async p => {
        const photo_id = p.photo ? (await uploadPhoto(p.photo, { kind: 'job', work_order_id: id, note: `Part: ${p.description}` })).id : undefined;
        const r = await api(`/api/work-orders/${id}/parts`, { body: { description: p.description, part_no: p.part_no, vendor: p.vendor, qty: p.qty, job_id: modal.part, photo_id } });
        setModal(null); toast(disposalNote(r, false) || 'Added — the office sets the price'); reload();
      } : undefined} />}
      {modal === 'body' && <BodyPicker wo={w} mech onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); toast('Sent — the office prices it'); }} />}
      {modal === 'report' && <ReportPart workOrderId={id} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); toast('Sent to office'); }} />}
      {modal === 'job' && <MechJobModal woId={id} trailer={!!w.trailer_id} onClose={() => setModal(null)} onDone={(start) => { setModal(null); reload(); toast(start ? 'Job added and started' : 'Job added'); }} />}
    </div>
  );
}

/** "1 h 25 m" */
export const fmtDur = (sec: number) => { const m = Math.max(0, Math.round(sec / 60)); return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60 ? (m % 60) + ' min' : ''}`.trim(); };
/** Seconds on a job so far, counting a running clock. */
export const jobSeconds = (j: Row, nowMs = Date.now()) => Number(j.worked_sec || 0) + (j.started_at ? Math.max(0, (nowMs - Date.parse(j.started_at)) / 1000) : 0);

function useTick(on: boolean) {
  const [, set] = useState(0);
  useEffect(() => { if (!on) return; const t = setInterval(() => set(x => x + 1), 30000); return () => clearInterval(t); }, [on]);
}

function PartLines({ parts, me, woId, locked, reload }: { parts: Row[]; me?: Me; woId?: string; locked?: boolean; reload?: () => void }) {
  const [open, setOpen] = useState<Row | null>(null);
  const can = !!me && !!woId && !locked;
  return <>
    <ul className="line-list">{parts.map(p => (
      <li key={p.id} className={can ? 'clickable' : ''} onClick={() => can && setOpen(p)}><span className="qty">{p.uom && p.uom !== 'each' ? qtyUom(p.qty, p.uom) : `${p.qty}×`}</span>
        <span className="grow"><b>{p.description}</b><small>{p.sku ? `${p.sku} · bin ${p.bin || '—'} · ` : ''}{p.added_by_name}</small>
          {p.linked_from && <small className="linked-tag">🔗 goes with {parts.find(x => x.id === p.linked_from)?.description || 'a part above'}</small>}
          {p.flag_note && <small className="flag-line">⚑ Told the office: {p.flag_note}</small>}</span>
        {can && <Icon name="edit" size={15} />}</li>))}</ul>
    {open && me && woId && <PartFix p={open} me={me} woId={woId} onClose={() => setOpen(null)} onDone={() => { setOpen(null); reload?.(); }} />}
  </>;
}

/** Tap a part line: change how many (only the parts you put on) or tell the office it's the wrong part. */
function PartFix({ p, me, woId, onClose, onDone }: { p: Row; me: Me; woId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const mine = p.added_by === me.user.id;
  const [qty, setQty] = useState(Number(p.qty) || 1);
  const [why, setWhy] = useState('');
  const [kind, setKind] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, msg: string) => { setBusy(true); try { await fn(); toast(msg); onDone(); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); } };
  return (
    <Modal title={p.description} onClose={onClose}>
      <div className="form-body">
        {mine ? <div className="full qty-fix">
          <span>How many did you use?</span>
          <div className="qty-step"><button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="One less">−</button><b>{qty}</b><button type="button" onClick={() => setQty(q => q + 1)} aria-label="One more">+</button></div>
          <Button kind="primary" disabled={busy || qty === Number(p.qty)} onClick={() => void run(() => api(`/api/work-orders/${woId}/parts/${p.id}/qty`, { method: 'PUT', body: { qty } }), `Changed to ${qty}`)}>Save</Button>
          <small className="muted">Fewer → the rest goes back on the shelf.</small>
        </div> : <p className="form-note full">{p.added_by_name ? `${p.added_by_name} put this part on` : 'The office put this part on'} — only they or the office can change how many.</p>}
        <div className="full"><b className="xw-h">Problem with this part? Tell the office</b>
          <div className="chip-row">{[['Defective', 'Defective — broken / bad out of the box'], ['Wrong part', 'Wrong part'], ['Not used', 'Not used — not needed']].map(([k, l]) => (
            <button key={k} type="button" className={'chip big' + (kind === k ? ' on' : '')} onClick={() => setKind(k)}>{l}</button>))}</div></div>
        <label className="field full"><span>What is wrong (optional)</span>
          <input value={why} onChange={e => setWhy(e.target.value)} placeholder={kind === 'Defective' ? 'e.g. bolt broken, leaking, cracked' : 'e.g. wrong size, put on the wrong job'} maxLength={280} /></label>
        <Button kind="danger" className="full" disabled={busy || !kind} onClick={() => void run(() => api(`/api/work-orders/${woId}/parts/${p.id}/flag`, { method: 'PUT', body: { note: `${kind}${why.trim() ? ': ' + why.trim() : ''}` } }), 'The office got it — they will take it off')}>⚑ Tell the office</Button>
      </div>
    </Modal>
  );
}

/** Note on a job for the office (extra time, stuck bolt, what else was found). */
function JobNote({ j, onClose, onDone }: { j: Row; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [note, setNote] = useState(j.mech_note || '');
  return (
    <Modal title={`Note — ${j.title}`} onClose={onClose}>
      <Form submitLabel="Send to office" onCancel={onClose} onSubmit={async () => { await api(`/api/jobs/${j.id}/mech-note`, { method: 'PUT', body: { note } }); toast('Note sent to the office'); onDone(); }}>
        <TextArea label="Note for the office" name="note_ui" value={note} onChange={e => setNote(e.target.value)} rows={4} placeholder="e.g. took 2 hours more — bolts seized; also saw a leak on the rear seal" />
      </Form>
    </Modal>
  );
}

function JobCard({ j, me, locked, trailer, parts, reload, onPart, onFittings, onBody, onPhoto, mechs, check, onCheck, woId }: {
  j: Row; me: Me; locked: boolean; trailer: boolean; parts: Row[]; reload: () => void; onPart: () => void; onFittings: () => void; onBody?: () => void; onPhoto: () => void;
  mechs?: Row[]; check?: Row; onCheck: () => void; woId: string;
}) {
  const toast = useToast();
  const running = j.status === 'doing' && !!j.started_at;
  useTick(running);
  const mine = j.assigned_to === me.user.id, free = !j.assigned_to;
  const [esc, setEsc] = useState(false);
  const [wait, setWait] = useState(false);
  const [noting, setNoting] = useState(false);
  const [more, setMore] = useState(false);
  const [extra, setExtra] = useState(false);
  const can = !locked && (mine || free);
  // The supervisor moves a job with the name picker instead of taking it over.
  const takeOver = !locked && !mine && !free && !mechs && (me.user.floor ? floorOk('take_over') : me.shop.mech_take_over !== '0');
  const partsOk = floorOk('parts_use') || floorOk('parts_new');
  // Helping on someone else's job: put a part on it (it's recorded under your name).
  const helpPart = !locked && !can && !mechs && partsOk;
  const sec = jobSeconds(j);
  const set = async (status: string, note?: string) => {
    try { await api(`/api/jobs/${j.id}/status`, { method: 'PUT', body: { status, note } }); toast(status === 'done' ? `${j.title} — done (${fmtDur(jobSeconds(j))})` : status === 'doing' ? (j.status === 'waiting' ? `Back on ${j.title}` : `Started ${j.title}`) : status === 'waiting' ? 'Waiting for parts — the office knows' : 'Back to to-do'); reload(); }
    catch (e) { toast((e as Error).message, 'err'); }
  };
  const assign = async (userId: string) => {
    try { await api(`/api/jobs/${j.id}/assign`, { method: 'PUT', body: { user_id: userId || null } }); toast(userId ? `Given to ${mechs?.find(m => m.id === userId)?.name || 'them'}` : 'Not given to anyone'); reload(); }
    catch (e) { toast((e as Error).message, 'err'); }
  };
  const pick = (fn: () => void) => () => { setMore(false); fn(); };
  return (
    <li className={(mine ? 'mine ' : '') + (j.status || 'todo')}>
      <div className="tj-head"><b>{trailer && j.on_trailer ? <span className="tag-trailer">Trailer</span> : null}{j.title}</b>
        {mechs && !locked ? <select className={'tj-assign' + (free ? ' free' : '')} value={j.assigned_to || ''} aria-label={`Who does ${j.title}`} onChange={e => void assign(e.target.value)}>
          <option value="">Not given to anyone</option>{mechs.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
          : <TechChip userId={j.assigned_to} name={j.assigned_name} meId={me.user.id} status={j.status} running={running} />}</div>
      {(sec >= 60 || running) && <small className="muted"><b className={running ? 'tj-live' : ''}>⏱ {fmtDur(sec)}</b>{running ? ` since ${fmtClock(j.started_at)}` : ''}</small>}
      {j.status === 'waiting' && <div className="tj-wait">⏸ Waiting for parts{j.waiting_note ? `: ${j.waiting_note}` : ''}</div>}
      {check && <div className="tj-pm"><ChecklistButton check={check} locked={locked} big onChanged={onCheck} /></div>}
      {j.check_status && <div className="tj-check"><CheckBadge job={j} /></div>}
      <CheckButtons job={j} onDone={reload} big />
      {parts.length > 0 && <PartLines parts={parts} me={me} woId={parts[0]?.work_order_id} locked={locked} reload={reload} />}
      {j.mech_note && <div className="tj-note">📝 {j.mech_note}</div>}
      {j.escalated_at && <div className="tj-esc"><b>⚑ Needs specialist</b> — {j.escalate_note}</div>}
      {!locked && (can || takeOver || helpPart) && <div className="tj-main">
        {can ? (j.status === 'waiting' ? <button className="btn primary big" onClick={() => set('doing')}>▶ Resume</button>
          : j.status === 'doing' ? <button className="btn danger big" onClick={() => set('done')}>■ End</button>
          : j.status === 'done' ? <button className="btn ghost big" onClick={() => set('doing')}>✓ Done · again</button>
          : <button className="btn success big" onClick={() => set('doing')}>▶ {free ? 'Take & Start' : 'Start'}</button>)
          : takeOver && <button className="btn secondary big" onClick={pick(async () => {
            if (!confirmAction(running ? `${j.assigned_name} is working on "${j.title}" right now. Take it over? Their clock stops and their time stays theirs; yours starts now.` : `Take over "${j.title}" from ${j.assigned_name}? Their time so far stays theirs.`)) return;
            try { await api(`/api/jobs/${j.id}/take-over`, { body: {} }); toast(`${j.title} is yours now`); reload(); } catch (e) { toast((e as Error).message, 'err'); }
          })}>Take over</button>}
        {(can || helpPart) && partsOk && <button className="btn secondary big" onClick={onPart}>+ Part</button>}
        {can && <button className="btn secondary big" onClick={onPhoto}>📷 Photo</button>}
      </div>}
      {!locked && can && <button type="button" className="tj-more" onClick={() => setMore(true)}>⋯ More</button>}
      {more && <div className="sheet-dim" onClick={() => setMore(false)}>
        <div className="sheet" onClick={e => e.stopPropagation()} role="dialog" aria-label={`More — ${j.title}`}>
          <div className="sheet-bar" /><b className="sheet-title">{j.title}</b>
          {(j.status === 'doing' || j.status === 'todo' || !j.status) && !free && <button onClick={pick(() => setWait(true))}>⏸ Waiting for parts</button>}
          <button onClick={pick(() => setExtra(true))}>➕ Extra work — seized bolts, rust…</button>
          <button onClick={pick(() => setNoting(true))}>📝 Note to the office</button>
          {!j.escalated_at && j.status !== 'done' && <button onClick={pick(() => setEsc(true))}>⚑ Needs a specialist</button>}
          {partsOk && <button onClick={pick(onFittings)}>🔩 Fittings — pick by photo</button>}
          {onBody && <button onClick={pick(onBody)}>🚛 Body damage</button>}
          {j.status === 'doing' && <button onClick={pick(() => set('todo'))}>↩ Stop — not started yet</button>}
          <button className="sheet-cancel" onClick={() => setMore(false)}>Cancel</button>
        </div></div>}
      {noting && <JobNote j={j} onClose={() => setNoting(false)} onDone={() => { setNoting(false); reload(); }} />}
      {extra && <ExtraWork j={j} woId={woId} onClose={() => setExtra(false)} onDone={() => { setExtra(false); toast('Sent — the office adds it to the job'); reload(); }} />}
      {esc && <EscalateModal job={j} onClose={() => setEsc(false)} onDone={() => { setEsc(false); toast('Sent to the office — they will bring a specialist'); reload(); }} />}
      {wait && (
        <Modal title={`Waiting for parts — ${j.title}`} onClose={() => setWait(false)}>
          <Form submitLabel="Pause — waiting for parts" onCancel={() => setWait(false)} onSubmit={async v => { await set('waiting', v.note); setWait(false); }}>
            <Field label="Which part?" name="note" full required autoFocus placeholder="e.g. Air dryer cartridge, 2 brake chambers" />
            <p className="form-note full">Your clock stops. The office gets a message and orders it; you get a message when it's here — then press Resume.</p>
          </Form>
        </Modal>
      )}
    </li>
  );
}

/** Mechanic can't solve it: what was checked goes to the office so the specialist doesn't start from zero. */
export function EscalateModal({ job, onClose, onDone }: { job: Row; onClose: () => void; onDone: () => void }) {
  return (
    <Modal title={`Escalate: ${job.title}`} onClose={onClose}>
      <Form submitLabel="Send to office" onCancel={onClose} onSubmit={async v => { await api(`/api/jobs/${job.id}/escalate`, { body: v }); onDone(); }}>
        <TextArea label="What we checked / what we found *" name="note" required placeholder="Codes, tests done, what is OK, what is not. E.g. SPN 639 FMI 9. Batteries, grounds, fuses OK. ECM loses power randomly." />
        <Select label="What kind of specialist?" name="kind" full>
          {['Electrical / ECM', 'Engine', 'Aftertreatment (DPF / DEF / EGR)', 'Transmission', 'Brakes / ABS', 'A/C', 'Hydraulics', 'Other'].map(k => <option key={k}>{k}</option>)}
        </Select>
        <p className="form-note full">Tip: add photos of the codes or the problem with the Photo button.</p>
      </Form>
    </Modal>
  );
}

function MechJobModal({ woId, trailer, onClose, onDone }: { woId: string; trailer: boolean; onClose: () => void; onDone: (started: boolean) => void }) {
  const presets = ['Tire change', 'Oil change', 'PM service', 'Brake job', 'Air leak', 'Lights', 'Diagnose', 'Alignment'];
  const [title, setTitle] = useState('');
  const [side, setSide] = useState<'truck' | 'trailer'>('truck');
  return (
    <Modal title="Add job" onClose={onClose}>
      <Form submitLabel="Add & Start" extraSubmit={{ label: 'Just add', value: 'add' }} onCancel={onClose} onSubmit={async v => {
        const r = await api<{ id: string }>(`/api/work-orders/${woId}/jobs`, { body: { title, on_trailer: side === 'trailer' } });
        const start = v._action !== 'add';
        if (start) await api(`/api/jobs/${r.id}/status`, { method: 'PUT', body: { status: 'doing' } });
        onDone(start);
      }}>
        <label className="field full"><span>What job? *</span>
          <input value={title} onChange={e => setTitle(e.target.value)} required maxLength={200} placeholder="e.g. Tire change" autoFocus /></label>
        <div className="chip-row full">{presets.map(p => <button type="button" key={p} className={'chip' + (title === p ? ' on' : '')} onClick={() => setTitle(p)}>{p}</button>)}</div>
        {trailer && <div className="seg-choice full" role="radiogroup" aria-label="Truck or trailer">
          {(['truck', 'trailer'] as const).map(x => <button type="button" key={x} role="radio" aria-checked={side === x} className={side === x ? 'on' : ''} onClick={() => setSide(x)}>{x === 'truck' ? 'Truck' : 'Trailer'}</button>)}</div>}
      </Form>
    </Modal>
  );
}

/** For parts that aren't in the system (bought from a store, from the truck, etc.): photo + note → office review queue. */
function ReportPart({ workOrderId, onClose, onDone }: { workOrderId: string; onClose: () => void; onDone: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <Modal title="Part not in the list" onClose={onClose}>
      <div className="form">
        <div className="form-body">
          <p className="form-note full">Take a photo of the part, box or receipt and say how many. The office adds it to the invoice.</p>
          <input ref={ref} type="file" accept="image/*" capture="environment" hidden onChange={e => {
            const f = e.target.files?.[0] || null; setFile(f); setPreview(f ? URL.createObjectURL(f) : '');
          }} />
          {preview ? <img src={preview} className="preview full" alt="" onClick={() => ref.current?.click()} />
            : <div className="photo-two full"><button type="button" className="btn secondary big" onClick={() => ref.current?.click()}><Icon name="camera" size={20} />Take photo</button>
              <button type="button" className="btn secondary big" onClick={() => { if (ref.current) { ref.current.removeAttribute('capture'); ref.current.click(); setTimeout(() => ref.current?.setAttribute('capture', 'environment'), 1000); } }}>🖼 Gallery</button></div>}
          <label className="field full"><span>What part, how many? *</span>
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. 2 air hoses 3/8, bought at NAPA" /></label>
        </div>
        {err && <div className="error-box"><Icon name="alert" />{err}</div>}
        <div className="form-foot"><span className="spacer" /><Button onClick={onClose}>Cancel</Button>
          <Button kind="primary" busy={busy} disabled={!file || !note.trim()} onClick={async () => {
            setBusy(true); setErr('');
            try { await uploadPhoto(file!, { kind: 'part_used', work_order_id: workOrderId, note }); onDone(); } catch (e) { setErr((e as Error).message); setBusy(false); }
          }}>Send to office</Button></div>
      </div>
    </Modal>
  );
}

function PartsLookup() {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 200);
  const { data, error } = useApi<Row[]>(`/api/parts?q=${encodeURIComponent(dq)}`, [dq]);
  return (
    <>
      <h1 className="floor-h1">Parts lookup</h1>
      <SearchBox value={q} onChange={setQ} placeholder="Part #, name or bin…" />
      <ErrorBox error={error} />
      {!data ? <Loading /> : (
        <ul className="line-list">
          {data.slice(0, 200).map(p => (
            <li key={p.id}><PhotoThumbs ids={partPhotos(p)} /><span className="grow"><b>{p.name}</b><small>{p.sku}</small></span>
              <span className="bin">Bin {p.bin || '—'}</span>{p.track_qty === 0 ? <span className="stock" title="Not counted">bulk</span> : <span className={'stock ' + (p.stock <= 0 ? 'out' : '')}>{qtyUom(p.stock, p.uom)}</span>}</li>
          ))}
          {!data.length && <li className="muted">No parts found.</li>}
        </ul>
      )}
      <p className="muted small">To take a part, open the job and tap “Use part” so it goes on the customer's bill.</p>
    </>
  );
}

/** Shelf count (only for roles with "Counts inventory"): Count & Scan — no prices, no system quantity. */
function CountShelf() {
  const mine = useApi<Row[]>('/api/count/mine');
  return (
    <>
      <h1 className="floor-h1">Count shelf</h1>
      <ScanCount onSaved={() => mine.reload()} />
      <h2 className="sec">Counted by you ({(mine.data || []).length})</h2>
      <ul className="line-list">{(mine.data || []).map(d => <li key={d.part_id}><span className="grow"><b>{d.name}</b><small>{d.sku}{d.bin ? ` · Bin ${d.bin}` : ''}</small></span><span className="stock">{d.counted}</span></li>)}
        {mine.data && !mine.data.length && <li className="muted">Nothing counted yet.</li>}</ul>
      <p className="muted small">The office checks the counts and applies them.</p>
    </>
  );
}

/** Store runs (Harbor Freight, Home Depot…): photo of the receipt goes to the owner for OK. */
function Receipts() {
  const { data, error, reload } = useApi<Row[]>('/api/expenses/mine');
  const [adding, setAdding] = useState(false);
  const toast = useToast();
  return (
    <>
      <h1 className="floor-h1">Store receipts</h1>
      <p className="muted small">Bought something for the shop — blades, bits, cleaner, a tool? Take a photo of the receipt so it's counted. Paid with your own money? Say so and the office pays you back.</p>
      <button className="btn primary huge" onClick={() => setAdding(true)}><Icon name="camera" size={22} />Add receipt</button>
      <ErrorBox error={error} onRetry={reload} />
      <h2 className="sec">Sent by you</h2>
      {!data ? <Loading /> : data.length ? (
        <ul className="line-list receipt-list">
          {data.map(e => (
            <li key={e.id}><ReceiptThumb id={e.photo_id} />
              <span className="grow"><b>{e.vendor} · {dollars(e.amount)}</b><small>{fmtDate(e.spent_on)} · {e.description || e.category}</small></span>
              {e.status === 'pending' ? <Badge tone="amber">Waiting</Badge> : e.paid_by === 'worker' ? (e.reimbursed_on ? <Badge tone="green">Paid back</Badge> : <Badge tone="blue">OK · to pay back</Badge>) : <Badge tone="green">OK</Badge>}
              {e.status === 'pending' && <button className="icon-btn" aria-label="Delete" onClick={async () => {
                if (!confirmAction('Delete this receipt?')) return;
                await api(`/api/expenses/${e.id}`, { method: 'DELETE' }); toast('Deleted'); reload();
              }}><Icon name="trash" size={18} /></button>}
            </li>
          ))}
        </ul>
      ) : <Empty>No receipts yet.</Empty>}
      {adding && <AddReceipt onClose={() => setAdding(false)} onDone={() => { setAdding(false); toast('Sent to office'); reload(); }} />}
    </>
  );
}

function AddReceipt({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [pre, setPre] = useState<Row | null>(null);
  const [reading, setReading] = useState(false);
  const toast = useToast();
  return (
    <Modal title="Store receipt" onClose={onClose}>
      {/* AI photo reading is office-only — the office fills it in from the photo when it checks the receipt. */}
      <Form key={pre ? 'ai' : 'manual'} submitLabel="Send to office" onCancel={onClose} onSubmit={async v => {
        if (!file) throw new Error('Take a photo of the receipt first');
        const id = pre?.photo_id || (await uploadPhoto(file, { kind: 'receipt', purpose: 'expense' })).id;
        await api('/api/expenses', { body: { ...v, amount: toCents(v.amount), photo_id: id } });
        onDone();
      }}>
        <ReceiptPicker file={file} onChange={f => { setFile(f); setPre(null); }} required />
        <StoreField required defaultValue={pre?.store || ''} />
        <MoneyField label="Total on receipt" name="amount" required full defaultValue={pre?.total ? (pre.total / 100).toFixed(2) : ''} />
        <Select label="What kind" name="category" full defaultValue="Blades, bits & small tools">{PURCHASE_CATEGORIES.map(c => <option key={c}>{c}</option>)}</Select>
        <TextArea label="What did you buy?" name="description" placeholder="e.g. sawzall blades, 2 cut-off wheels" />
        <Select label="Who paid?" name="paid_by" full><option value="shop">Shop card / shop cash</option><option value="worker">My own money — pay me back</option></Select>
      </Form>
    </Modal>
  );
}

function Clock({ data, reload }: { data: Row | null; reload: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick(x => x + 1), 30000); return () => clearInterval(t); }, []);
  if (!data) return <Loading />;
  const open = data.open as Row | null;
  const running = open ? Math.round((Date.now() - Date.parse(open.clock_in)) / 36000) : 0;
  async function act(path: string, msg: string) {
    setBusy(true);
    try { await api(path, { body: {} }); toast(msg); reload(); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
  }
  return (
    <div className="clock">
      <div className={'clock-face ' + (open ? 'on' : '')}>
        <small>{open ? 'Clocked in since' : 'You are'}</small>
        <strong>{open ? fmtClock(open.clock_in) : 'Clocked out'}</strong>
        {open && <span>{hours(running)} h{open.wo_number ? ` · on ${open.wo_number}` : ''}</span>}
      </div>
      {open
        ? <button className="btn danger huge" disabled={busy} onClick={() => act('/api/clock/out', 'Clocked out')}>Clock out</button>
        : <button className="btn primary huge" disabled={busy} onClick={() => act('/api/clock/in', 'Clocked in')}>Clock in</button>}
      <div className="clock-stats">
        <div><small>Today</small><b>{hours(data.today)} h</b></div>
        <div><small>This week</small><b>{hours(data.week)} h</b></div>
      </div>
      <h2 className="sec">Last 2 weeks</h2>
      <ul className="line-list small">
        {(data.entries as Row[]).map(e => (
          <li key={e.id}><span className="grow"><b>{new Date(e.clock_in).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</b>
            <small>{fmtClock(e.clock_in)} – {e.clock_out ? fmtClock(e.clock_out) : 'now'}{e.wo_number ? ` · ${e.wo_number}` : ''}</small></span>
            <span>{hours(Math.round(((e.clock_out ? Date.parse(e.clock_out) : Date.now()) - Date.parse(e.clock_in)) / 36000))} h</span></li>
        ))}
        {!data.entries.length && <li className="muted">No time recorded yet.</li>}
      </ul>
      <p className="muted small">Forgot to clock in or out? Tell the office — they can fix it.</p>
    </div>
  );
}

function Inbox({ onRead }: { onRead: () => void }) {
  const { data, error, reload } = useApi<Row>('/api/notifications');
  useEffect(() => { if (data?.unread) void api('/api/notifications/read', { body: {} }).then(onRead); }, [data?.unread]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  return (
    <>
      <h1 className="floor-h1">Notifications</h1>
      <PushBanner />
      {(data.items as Row[]).length ? <ul className="inbox">{(data.items as Row[]).map(n => (
        <li key={n.id} className={n.read_at ? '' : 'new'} onClick={() => n.link && navigate(n.link)}><b>{n.title}</b><span>{n.body}</span><small className="muted">{ago(n.created_at)}</small></li>
      ))}</ul> : <Empty>Nothing yet. When the office gives you a job, it shows up here.</Empty>}
    </>
  );
}

const pushSupported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
function keyBytes(k: string) { const t = k.replace(/-/g, '+').replace(/_/g, '/'); const b = atob(t + '='.repeat((4 - (t.length % 4)) % 4)); return Uint8Array.from(b, c => c.charCodeAt(0)); }

/** Turn on phone notifications for this phone. Returns an error message, or null when it worked. */
async function enablePush(): Promise<string | null> {
  if (!pushSupported()) return 'This phone can\'t get notifications from the browser. On iPhone: Safari → Share → "Add to Home Screen", then open the app from the home screen and try again.';
  const { key } = await api<{ key: string | null }>('/api/push/key');
  if (!key) return 'Phone notifications are not set up on the server yet — ask the office. You still see new jobs here.';
  if ((await Notification.requestPermission()) !== 'granted') return 'Notifications are blocked. Allow them for this app in the phone settings.';
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
  await api('/api/push/subscribe', { body: sub.toJSON() as Row });
  return null;
}

function usePushState() {
  const [state, setState] = useState<'unknown' | 'on' | 'off' | 'unsupported' | 'noserver'>('unknown');
  useEffect(() => {
    let live = true;
    (async () => {
      if (!pushSupported()) return live && setState('unsupported');
      const { key } = await api<{ key: string | null }>('/api/push/key').catch(() => ({ key: null }));
      if (!key) return live && setState('noserver');
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (live) setState(sub && Notification.permission === 'granted' ? 'on' : 'off');
    })();
    return () => { live = false; };
  }, []);
  return [state, setState] as const;
}

function PushBanner({ always }: { always?: boolean }) {
  const [state, setState] = usePushState();
  const toast = useToast();
  if (state === 'on' || state === 'unknown' || (!always && state === 'noserver')) return null;
  return (
    <div className="push-banner">
      <Icon name="bell" size={18} />
      <span>{state === 'noserver' ? 'Phone notifications are not set up on the server yet.' : 'Get a notification on this phone when a job is given to you.'}</span>
      {state !== 'noserver' && <Button kind="primary" onClick={async () => { try { const err = await enablePush(); if (err) toast(err, 'err'); else { setState('on'); toast('Notifications are on'); } } catch (e) { toast((e as Error).message, 'err'); } }}>Turn on</Button>}
    </div>
  );
}

function MePage({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const toast = useToast();
  return (
    <>
      <h1 className="floor-h1">{me.user.name}</h1>
      <p className="muted">Signed in as {me.user.login}</p>
      {floorOk('receipts') && <Button className="full" icon="invoice" onClick={() => navigate('/floor/receipts')}>Receipts — I bought something for the shop</Button>}
      <h2 className="sec">Notifications</h2>
      <PushBanner always />
      <h2 className="sec">Time off</h2>
      <MyTimeOff />
      {me.user.role !== 'mechanic' && <Button className="full" icon="home" onClick={() => navigate('/')}>Back to office view</Button>}
      <h2 className="sec">Change password</h2>
      <Form submitLabel="Change password" onSubmit={async (v, f) => {
        if (v.password !== v.password2) throw new Error('New passwords do not match');
        await api('/api/me/password', { body: v }); f.reset(); toast('Password changed');
      }}>
        <Field label="Current password" name="current" type="password" required full autoComplete="current-password" />
        <Field label="New password" name="password" type="password" minLength={6} required full autoComplete="new-password" />
        <Field label="Repeat new password" name="password2" type="password" minLength={6} required full autoComplete="new-password" />
      </Form>
      <h2 className="sec">Install on your phone</h2>
      <p className="muted small">iPhone: Safari → Share → “Add to Home Screen”. Android: Chrome menu → “Install app”. It opens like a normal app.</p>
      <Button kind="danger" className="full" icon="logout" onClick={onSignOut}>Sign out</Button>
    </>
  );
}

/** Shop board: every truck in the shop, each job, who is on it and for how long. View only for "see all, work on mine". */
function ShopBoard({ me }: { me: Me }) {
  const { data, error, reload } = useApi<Row>('/api/floor/board');
  useEffect(() => { const t = setInterval(() => { if (document.visibilityState === 'visible') reload(); }, 60000); return () => clearInterval(t); }, [reload]);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick(x => x + 1), 30000); return () => clearInterval(t); }, []);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const trucks = data.trucks as Row[];
  const all = trucks.flatMap(t => t.jobs as Row[]);
  const working = all.filter(j => j.status === 'doing' && j.started_at).length, waiting = all.filter(j => j.status === 'waiting').length, free = all.filter(j => !j.assigned_to && j.status !== 'done').length;
  const workAll = floorOk('see_all');
  const full = () => { const el = document.documentElement; if (document.fullscreenElement) void document.exitFullscreen(); else void el.requestFullscreen?.(); };
  return (
    <div className="shop-board">
      <div className="floor-home-head"><h1 className="floor-h1">Shop <small>· {trucks.length} truck{trucks.length === 1 ? '' : 's'}</small></h1>
        <button type="button" className="btn secondary" onClick={full}>⛶ Full screen</button></div>
      <div className="sb-sum"><span><b>{working}</b> working</span><span className="w"><b>{waiting}</b> waiting parts</span><span className="f"><b>{free}</b> not given to anyone</span></div>
      {!trucks.length ? <Empty>No trucks in the shop.</Empty> : <div className="sb-list">{trucks.map(t => {
        const jobs = t.jobs as Row[];
        const mineHere = jobs.some(j => j.assigned_to === me.user.id);
        const open = true;
        return (
          <div key={t.id as string} className={'sb-truck' + (open ? ' open' : '') + (mineHere ? ' mine' : '')} onClick={() => open && navigate('/floor/jobs/' + t.id)} role={open ? 'button' : undefined}>
            <div className="sb-head"><b className="sb-unit">#{t.unit || '—'}{t.trailer_unit ? <small> + #{t.trailer_unit}</small> : null}</b>
              <span className="sb-cust">{t.customer_name || ''}{t.bay ? ` · ${t.bay}` : ''}</span>{t.priority === 'Urgent' && <Badge tone="red">URGENT</Badge>}</div>
            {jobs.length ? <ul className="sb-jobs">{jobs.map(j => {
              const run = j.status === 'doing' && !!j.started_at;
              const st = run ? 'run' : j.status === 'waiting' ? 'wait' : j.status === 'done' ? 'done' : !j.assigned_to ? 'free' : 'todo';
              return <li key={j.id as string} className={st}>
                <span className="sb-title">{j.title}</span>
                <span className="sb-who">{st === 'run' ? '● ' : st === 'wait' ? '⏸ ' : st === 'done' ? '✓ ' : ''}{j.tech || 'nobody yet'}{j.assigned_to === me.user.id ? ' (you)' : ''}</span>
                <span className="sb-time">{st === 'wait' ? (j.waiting_note ? `waiting: ${j.waiting_note}` : 'waiting parts') : jobSeconds(j) >= 60 ? fmtDur(jobSeconds(j)) : ''}</span>
              </li>;
            })}</ul> : <p className="muted small">No jobs yet</p>}
          </div>);
      })}</div>}
      {!workAll && <p className="muted small">Tap a truck to see its jobs. You can take over a job or put a part on it — the office sees who did it.</p>}
    </div>
  );
}

/** Extra work on a job (seized bolts, rust…): what happened and how long — the office prices it. */
const EXTRA_REASONS = ['Seized / cut bolts', 'Rusted parts', 'Broken stud or bolt', 'Removed extra parts to reach it', 'Other'];
const EXTRA_TIMES: [number, string][] = [[15, '+15 min'], [30, '+30 min'], [60, '+1 h'], [120, '+2 h'], [180, '+3 h']];
function ExtraWork({ j, woId, onClose, onDone }: { j: Row; woId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [min, setMin] = useState(0);
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const cam = useRef<HTMLInputElement>(null);
  const what = reason === 'Other' ? other.trim() : reason;
  return (
    <Modal title={`Extra work — ${j.title}`} onClose={onClose}>
      <div className="form-body">
        <div className="full"><b className="xw-h">What happened?</b>
          <div className="chip-row">{EXTRA_REASONS.map(r => <button key={r} type="button" className={'chip big' + (reason === r ? ' on' : '')} onClick={() => setReason(r)}>{r}</button>)}</div>
          {reason === 'Other' && <input className="xw-other" value={other} onChange={e => setOther(e.target.value)} placeholder="What was it?" autoFocus />}</div>
        <div className="full"><b className="xw-h">How much extra time?</b>
          <div className="chip-row">{EXTRA_TIMES.map(([m, l]) => <button key={m} type="button" className={'chip big' + (min === m ? ' on' : '')} onClick={() => setMin(m)}>{l}</button>)}</div></div>
        <label className="field full"><span>Note (optional)</span><input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. 4 shock bolts seized — cut them off" maxLength={300} /></label>
        <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={e => setPhoto(e.target.files?.[0] || null)} />
        <button type="button" className="btn secondary full" onClick={() => cam.current?.click()}><Icon name="camera" size={16} />{photo ? '✓ Photo added — retake' : 'Photo (optional)'}</button>
        <p className="form-note full">The office sees it on this job and adds it to the bill — no prices here.</p>
      </div>
      <div className="form-foot"><Button onClick={onClose}>Cancel</Button><span className="spacer" />
        <Button kind="primary" busy={busy} disabled={!what || !min} onClick={async () => {
          setBusy(true);
          try {
            const photo_id = photo ? (await uploadPhoto(photo, { kind: 'job', work_order_id: woId, folder: 'diag', job_id: j.id, note: `Extra work: ${what}` })).id : undefined;
            await api(`/api/jobs/${j.id}/extra`, { body: { reason: what, minutes: min, note, photo_id } });
            onDone();
          } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
        }}>Send to office</Button></div>
    </Modal>
  );
}
